import { strFromU8, strToU8, unzipSync, zipSync } from 'fflate';
import { z } from 'zod';
import {
  allBackgrounds,
  documentAssetIds,
  parseProjectDocument,
  type Background,
  type ProjectDocument,
  type Site,
} from '@indusketch/project-model';
import { assetsApi, projectsApi } from '../../lib/api/endpoints.js';

/**
 * Portable project file (`.indusketch`): a zip with `project.json`, the project's own GLB
 * models under `models/` and the background image under `images/`, so another user can
 * import exactly the same planning.
 */
export const PROJECT_FILE_FORMAT = 'indusketch-project';
export const PROJECT_FILE_VERSION = 1;
export const PROJECT_FILE_EXTENSION = '.indusketch';

const manifestSchema = z.object({
  format: z.literal(PROJECT_FILE_FORMAT),
  version: z.number().int().min(1).max(PROJECT_FILE_VERSION),
  exportedAt: z.string(),
  models: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      file: z.string(),
      /** Added with background images; older files only contain 3D models. */
      kind: z.enum(['model', 'image']).default('model'),
    }),
  ),
  /** Validated and migrated separately by project-model, like every stored document. */
  document: z.unknown(),
});

export interface PackedModel {
  readonly id: string;
  readonly name: string;
  readonly data: Uint8Array;
  /** 3D model (GLB) or background image; defaults to a model. */
  readonly kind?: 'model' | 'image';
}

export const assetIdsOf = documentAssetIds;

export function packProject(doc: ProjectDocument, models: readonly PackedModel[]): Uint8Array {
  const files: Record<string, Uint8Array> = {};
  const entries = models.map((m) => {
    const kind = m.kind ?? 'model';
    const file = kind === 'image' ? `images/${m.id}` : `models/${m.id}.glb`;
    files[file] = m.data;
    return { id: m.id, name: m.name, file, kind };
  });
  const manifest = {
    format: PROJECT_FILE_FORMAT,
    version: PROJECT_FILE_VERSION,
    exportedAt: new Date().toISOString(),
    models: entries,
    document: doc,
  };
  files['project.json'] = strToU8(JSON.stringify(manifest, null, 2));
  return zipSync(files, { level: 6 });
}

export class ProjectFileError extends Error {}

/** Reads a `.indusketch` package or a plain `project.json`; the document is validated here. */
export function unpackProject(bytes: Uint8Array): {
  document: ProjectDocument;
  models: PackedModel[];
} {
  const isZip = bytes[0] === 0x50 && bytes[1] === 0x4b;
  let files: Record<string, Uint8Array> = {};
  let json: string;
  try {
    if (isZip) {
      files = unzipSync(bytes);
      const main = files['project.json'];
      if (!main) throw new ProjectFileError('Die Datei enthält kein InduSketch-Projekt.');
      json = strFromU8(main);
    } else {
      json = strFromU8(bytes);
    }
  } catch (err) {
    if (err instanceof ProjectFileError) throw err;
    throw new ProjectFileError('Die Datei ist beschädigt oder kein InduSketch-Projekt.');
  }
  let raw: unknown;
  try {
    raw = JSON.parse(json);
  } catch {
    throw new ProjectFileError('Die Datei ist beschädigt oder kein InduSketch-Projekt.');
  }
  const manifest = manifestSchema.safeParse(raw);
  if (!manifest.success)
    throw new ProjectFileError(
      'Die Datei ist kein InduSketch-Projekt oder stammt aus einer neueren Version.',
    );
  const parsed = parseProjectDocument(manifest.data.document);
  if (!parsed.ok)
    throw new ProjectFileError(`Projekt kann nicht importiert werden: ${parsed.error}`);
  const models = manifest.data.models.flatMap((m) => {
    const data = files[m.file];
    return data ? [{ id: m.id, name: m.name, data, kind: m.kind }] : [];
  });
  return { document: parsed.document, models };
}

/** Parcels and ground-floor site plan of a project file, to take them over into another project. */
export interface SitePlan {
  readonly name: string;
  readonly sites: readonly Site[];
  /** Still pointing at the file's image; see uploadSitePlan. */
  readonly background: Background | null;
  readonly models: readonly PackedModel[];
}

export function readSitePlan(bytes: Uint8Array): SitePlan {
  const { document, models } = unpackProject(bytes);
  return {
    name: document.name,
    sites: Object.values(document.sites),
    background: document.background,
    models,
  };
}

/**
 * Uploads the site plan's image (and the PDF it was drawn from) into projectId; returns the
 * background pointing at the uploaded files, or null when the file has no image for it.
 */
export async function uploadSitePlan(
  plan: SitePlan,
  projectId: string,
): Promise<Background | null> {
  const bg = plan.background;
  if (!bg) return null;
  const upload = async (assetId: string) => {
    const packed = plan.models.find((m) => m.id === assetId && m.kind === 'image');
    if (!packed) return null;
    const file = new File([packed.data as BlobPart], packed.name);
    return (await assetsApi.uploadImage(file, projectId)).image.id;
  };
  const assetId = await upload(bg.assetId);
  if (!assetId) return null;
  const pdfId = bg.pdf ? await upload(bg.pdf.assetId) : null;
  const { pdf: _pdf, ...rest } = bg;
  return { ...rest, assetId, ...(bg.pdf && pdfId ? { pdf: { ...bg.pdf, assetId: pdfId } } : {}) };
}

/** Points imported objects and the background at the files as they exist for the importing user. */
export function remapAssets(
  doc: ProjectDocument,
  ids: ReadonlyMap<string, string>,
): ProjectDocument {
  const objects = Object.fromEntries(
    Object.entries(doc.objects).map(([key, o]) => {
      if (o.source.kind !== 'asset') return [key, o];
      const assetId = ids.get(o.source.assetId) ?? o.source.assetId;
      return [key, { ...o, source: { ...o.source, assetId } }];
    }),
  );
  const remap = <T extends Background>(bg: T): T => ({
    ...bg,
    assetId: ids.get(bg.assetId) ?? bg.assetId,
    ...(bg.pdf ? { pdf: { ...bg.pdf, assetId: ids.get(bg.pdf.assetId) ?? bg.pdf.assetId } } : {}),
  });
  const background = doc.background ? remap(doc.background) : null;
  const wallImages = Object.fromEntries(
    Object.entries(doc.wallImages).map(([key, p]) => [
      key,
      { ...p, assetId: ids.get(p.assetId) ?? p.assetId },
    ]),
  );
  return {
    ...doc,
    objects,
    background,
    wallImages,
    ...(doc.levelBackgrounds ? { levelBackgrounds: doc.levelBackgrounds.map(remap) } : {}),
  };
}

const safeFileName = (name: string) => name.replace(/[\\/:*?"<>|]+/g, '_').trim() || 'Projekt';

/** Builds the export package, including every own 3D model the project uses. */
export async function exportProject(
  doc: ProjectDocument,
): Promise<{ blob: Blob; fileName: string }> {
  const available = new Map((await assetsApi.list()).assets.map((a) => [a.id, a]));
  const models: PackedModel[] = [];
  // Floor plans (also the PDFs they were drawn from) and wall pictures travel as images, the
  // rest as models.
  const plans = allBackgrounds(doc);
  const pictures = Object.values(doc.wallImages);
  for (const id of assetIdsOf(doc)) {
    const plan = plans.find((b) => b.assetId === id || b.pdf?.assetId === id);
    const picture = pictures.find((p) => p.assetId === id);
    const image = plan !== undefined || picture !== undefined;
    const res = await fetch(assetsApi.fileUrl(id), { credentials: 'same-origin' });
    if (!res.ok)
      throw new ProjectFileError(
        image
          ? 'Ein Bild des Projekts konnte nicht geladen werden.'
          : 'Ein 3D-Modell des Projekts konnte nicht geladen werden.',
      );
    const name = plan
      ? plan.pdf?.assetId === id
        ? plan.name
        : `${plan.name.replace(/\.pdf$/i, '')}${plan.pdf ? '.png' : ''}`
      : picture
        ? picture.name
        : (available.get(id)?.name ?? 'Modell');
    models.push({
      id,
      name,
      data: new Uint8Array(await res.arrayBuffer()),
      kind: image ? 'image' : 'model',
    });
  }
  const bytes = packProject(doc, models);
  return {
    blob: new Blob([bytes as BlobPart], { type: 'application/zip' }),
    fileName: `${safeFileName(doc.name)}${PROJECT_FILE_EXTENSION}`,
  };
}

/**
 * Creates a new project from a file. Models the user can already access (own or system
 * library) are reused; missing ones are uploaded as the user's own models.
 */
export async function importProject(file: File): Promise<string> {
  const { document, models } = unpackProject(new Uint8Array(await file.arrayBuffer()));
  const available = new Set((await assetsApi.list()).assets.map((a) => a.id));
  const packed = new Set(models.map((m) => m.id));
  const missing = assetIdsOf(document).filter((id) => !available.has(id) && !packed.has(id));
  if (missing.length > 0)
    throw new ProjectFileError(
      `${String(missing.length)} eigene 3D-Modelle fehlen in der Datei – bitte das Projekt als .indusketch-Paket exportieren.`,
    );
  const ids = new Map<string, string>();
  for (const m of models) {
    if (m.kind === 'image') {
      const uploaded = await assetsApi.uploadImage(new File([m.data as BlobPart], m.name));
      ids.set(m.id, uploaded.image.id);
      continue;
    }
    if (available.has(m.id)) continue;
    const name = /\.glb$/i.test(m.name) ? m.name : `${m.name}.glb`;
    const uploaded = await assetsApi.upload(
      new File([m.data as BlobPart], name, { type: 'model/gltf-binary' }),
    );
    ids.set(m.id, uploaded.asset.id);
  }
  const created = await projectsApi.create({
    name: document.name,
    description: document.description,
  });
  const doc = { ...remapAssets(document, ids), id: created.project.id };
  await projectsApi.save(created.project.id, created.project.revision, doc);
  return created.project.id;
}
